import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PaginatedResponseType } from '../Types/Response/PaginatedResponseType';
import axiosAuthorised, { DEV_OVERRIDE_HEADER } from './AxiosAuthorised';
import { PaginationPageParameters } from './PaginationHelpers';

export enum UsersUpdateType {
	ADD,
	REMOVE,
	DISABLE,
}

export interface UserDataType {
	clientId: number;
	isDisabled: boolean;
	name: string;
	roles: Array<string>;
	userId: number;
	userName: string;
}

export type AuthedSendsUser = {
	id: string;
	name: string;
	emailAddress: string;
	roles: Array<string>;
	permissions: Array<string>;
	client: {
		clientId: number;
		clientIdNo: string;
		balance: number;
		name: string;
		companyName: string;
	};
};

const addUser = async (userData: any) => {
	const { data } = await axiosAuthorised.post(
		`${process.env.REACT_APP_API_HOST}/client/v1/user`,
		JSON.stringify(userData),
		DEV_OVERRIDE_HEADER
	);

	return data;
};

const getUser = async (userId: number) => {
	const { data } = await axiosAuthorised.get(
		`${process.env.REACT_APP_API_HOST}/client/v1/user/${userId}`,
		DEV_OVERRIDE_HEADER
	);

	return data;
};

const deleteUser = async (userId: number) => {
	const { data } = await axiosAuthorised.delete(
		`${process.env.REACT_APP_API_HOST}/client/v1/user/${userId}`,
		DEV_OVERRIDE_HEADER
	);

	return data;
};

const disableUser = async (userId: number, disabled: boolean) => {
	const { data } = await axiosAuthorised.patch(
		`${process.env.REACT_APP_API_HOST}/client/v1/user/${userId}/disabled`,
		JSON.stringify({ isDisabled: disabled }),
		DEV_OVERRIDE_HEADER
	);

	return data;
};

const sendPasswordResetEmail = async (userId: number) => {
	const { data } = await axiosAuthorised.post(
		`${process.env.REACT_APP_API_HOST}/client/v1/user/${userId}/send-password-reset-email`,
		null,
		DEV_OVERRIDE_HEADER
	);

	return data;
};
const checkEmailAddressUnique = async (email: string) => {
	const { data } = await axiosAuthorised.get(
		`${process.env.REACT_APP_API_HOST}/client/v1/user/check-email-address-unique?emailAddress=${encodeURIComponent(
			email
		)}`,
		DEV_OVERRIDE_HEADER
	);

	return data;
};

const checkPhoneNumberValid = async (telephone: string) => {
	const valid: any = await axiosAuthorised
		.get(
			`${process.env.REACT_APP_API_HOST}/client/v1/user/check-phone-number-valid?value=${telephone.replace(
				'+',
				'%2B'
			)}`,
			DEV_OVERRIDE_HEADER
		)
		.then((response) => response.data.isValid)
		.catch((e) => {
			return false;
		});

	return valid;
};

const useUser = () => {
	return useQuery(
		['sendsUser'],
		async () => {
			const { data }: { data: AuthedSendsUser } = await axiosAuthorised.get(
				`${process.env.REACT_APP_API_HOST}/client/v1/user`,
				DEV_OVERRIDE_HEADER
			);

			return data;
		},
		{
			staleTime: 600000,
		}
	);
};

const useInfiniteUsers = () => {
	return useInfiniteQuery(
		['allUsers'],
		async ({ pageParam = `${process.env.REACT_APP_API_HOST}/client/v1/user/list?offset=0&limit=100` }) => {
			const { data }: { data: PaginatedResponseType } = await axiosAuthorised.get(pageParam, DEV_OVERRIDE_HEADER);
			return data;
		},
		{
			...PaginationPageParameters,
			staleTime: 600000,
		}
	);
};

const useUpdateUsers = () => {
	const queryClient = useQueryClient();
	let QUERY_KEY = ['allUsers'];

	return useMutation(
		async ({ forwardedFunction, newData }) => (forwardedFunction ? forwardedFunction(newData) : null),
		{
			onMutate: async ({
				newData,
				updateType,
			}: {
				forwardedFunction: any;
				newData: any;
				updateType: UsersUpdateType;
			}) => {
				await queryClient.cancelQueries(QUERY_KEY);

				const previousData = queryClient.getQueryData(QUERY_KEY);

				let modifiedData: any = queryClient.getQueryData(QUERY_KEY);

				if (modifiedData) {
					switch (updateType) {
						case UsersUpdateType.ADD:
							modifiedData.pages[modifiedData.pages.length - 1].data = [
								newData,
								...modifiedData.pages[modifiedData.pages.length - 1].data,
							];
							break;
						case UsersUpdateType.DISABLE:
							console.log('IMPLEMENT DISABLE');
							break;
						case UsersUpdateType.REMOVE:
							modifiedData.pages.map((group: any, index: number) => {
								modifiedData.pages[index].data = group.data.filter((userEntry: UserDataType) => {
									return newData !== userEntry.userId;
								});
							});
							break;
					}

					queryClient.setQueryData(QUERY_KEY, modifiedData);
				}

				return { previousData, modifiedData };
			},
			onError: (err, newData: any, context) => {
				queryClient.setQueryData(QUERY_KEY, context?.previousData);
			},
		}
	);
};

export {
	addUser,
	getUser,
	deleteUser,
	disableUser,
	sendPasswordResetEmail,
	checkEmailAddressUnique,
	checkPhoneNumberValid,
	useUser,
	useInfiniteUsers,
	useUpdateUsers,
};
